/**
 * ActivityFeedCard — "멘션 & 활동" 피드 (대시보드 재설계 Sprint 3)
 *
 * 기존 "알림 및 태그된 항목"의 후속. customer_event 는 액션 큐("오늘의 할 일")가
 * 담당하므로 백엔드가 기본 제외 — 여기는 멘션·댓글·상태변경 중심 (중복 해소).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronRight, CheckCheck, AtSign, MessageSquare, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/shadcn/button';
import EmptyState from '../../../components/ui/EmptyState';
import Tooltip from '../../../components/ui/Tooltip';
import { wsAvatarTone, wsInitials, wsTone } from '../../../components/ws';
import dashboardService, { DashboardActivity } from '../../../services/dashboard';
import notificationService from '../../../services/notifications';
import { useAuth } from '../../../context/AuthContext';

const REFERENCE_LABEL: Record<string, string> = {
  happy_call: '해피콜',
  claim: '클레임',
  sales_order: '영업주문',
  purchase_order: '구매주문',
  production_order: '생산주문',
  home_styling: '홈스타일링',
  approval_request: '결재',
};

/**
 * 항목 클릭 시 이동할 경로.
 * reference_url 이 비어 있는 과거 알림(결재 요청은 2026-08-04 수정 전까지 전부 null)도
 * reference_type + reference_id 로 이동 경로를 유도해 "클릭해도 아무 반응 없음" 을 없앤다.
 */
export function resolveActivityUrl(activity: DashboardActivity): string | null {
  if (activity.reference_url) return activity.reference_url;
  if (activity.reference_type === 'approval_request' && activity.reference_id) {
    const event = activity.metadata?.event;
    const scope = event === 'approval_pending' || event === 'approval_delegated' ? 'incoming' : 'mine';
    return `/hr/approvals?scope=${scope}&requestId=${activity.reference_id}`;
  }
  return null;
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  mention: <AtSign size={16} />,
  comment: <MessageSquare size={16} />,
  status_change: <RefreshCw size={16} />,
};

/** "X님이 ..." 제목에서 행위자 추출 (레거시 fallback) */
function extractActorFromTitle(title?: string): string | null {
  if (!title) return null;
  const m = title.match(/^(.+?)님이/);
  return m?.[1] || null;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

interface Props {
  /** 값이 바뀌면 재조회 (페이지 레벨 새로고침 연동) */
  refreshTick?: number;
}

/** 묶인 활동 — 같은 말이 여러 번 온 것을 한 줄로 접는다. */
interface GroupedActivity {
  head: DashboardActivity;
  count: number;
}

/**
 * 같은 제목·같은 사람이 잇달아 오면 한 줄로 묶는다.
 *
 * ⛔ 안 묶으면 똑같은 문장 여덟 줄이 그대로 쌓인다 — 2026-09-10 실측에서
 *   「회의록 열람 요청이 도착했습니다」가 8줄 연속으로 있었고, 그게 화면의 절반이었다.
 *   여덟 줄을 읽어도 알 수 있는 것은 한 줄과 같다. 그럴 바에는 「×8」이 낫다.
 * ⛔ 잇달아 온 것만 묶는다. 사이에 다른 일이 끼어 있으면 시간 순서가 깨진다.
 */
export function groupActivities(list: DashboardActivity[]): GroupedActivity[] {
  const out: GroupedActivity[] = [];
  for (const a of list) {
    const prev = out[out.length - 1];
    const sameActor = (prev?.head.metadata?.actor_name || '') === (a.metadata?.actor_name || '');
    if (prev && prev.head.title === a.title && sameActor) {
      prev.count += 1;
      continue;
    }
    out.push({ head: a, count: 1 });
  }
  return out;
}

const RowSkeleton: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderTop: '1px solid var(--ws-line-soft)' }}>
    <div className="ws-skel" style={{ width: 46, height: 46, borderRadius: 12 }} />
    <div style={{ flex: 1 }}>
      <div className="ws-skel" style={{ height: 13, width: '70%' }} />
      <div className="ws-skel" style={{ height: 11, width: '45%', marginTop: 8 }} />
    </div>
  </div>
);

/**
 * 오른쪽 칸 «멘션 & 활동» — 레퍼런스 «NEW MATCHES · FOUND OVERNIGHT» 카드 모양.
 * 왼쪽 네모 = 보낸 사람 머리글자(없으면 종류 아이콘), 가운데 제목·내용, 오른쪽 메뉴·시간.
 */
export const ActivityFeedCard: React.FC<Props> = ({ refreshTick = 0 }) => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const [activities, setActivities] = useState<DashboardActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await dashboardService.getActivities(20);
      setActivities(data);
    } catch (e: any) {
      setError(e?.message || '활동 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const markAll = useCallback(async () => {
    if (!authState.user?.id) return;
    await notificationService.markAllAsRead(authState.user.id).catch(() => {});
    load();
  }, [authState.user?.id, load]);

  const groups = groupActivities(activities).slice(0, 6);

  return (
    <div data-testid="activity-feed" className="ws-bezel">
      <div className="ws-card" style={{ padding: '20px 22px 18px' }}>
        <div className="ws-eyebrow" style={{ color: 'var(--ws-blue-ink)', display: 'flex', alignItems: 'center', gap: 7 }}>
          <Sparkles size={14} aria-hidden />
          멘션 & 활동 · 최근
        </div>

        <div style={{ marginTop: 14 }}>
          {loading ? (
            <><RowSkeleton /><RowSkeleton /><RowSkeleton /></>
          ) : error ? (
            <EmptyState variant="error" title="활동을 불러오지 못했습니다" description={error} onRetry={load} />
          ) : activities.length === 0 ? (
            <EmptyState title="새 알림이 없습니다" description="멘션·댓글이 오면 여기에 표시됩니다" />
          ) : (
            groups.map(({ head: activity, count }) => {
              const actorName = activity.metadata?.actor_name || extractActorFromTitle(activity.title);
              const menuLabel = activity.metadata?.menu_label
                || REFERENCE_LABEL[activity.reference_type || '']
                || null;
              const typeIcon = TYPE_ICON[activity.type || ''] || <Bell size={16} />;
              const targetUrl = resolveActivityUrl(activity);
              const tone = actorName ? wsAvatarTone(actorName) : 'gray';
              return (
                <div
                  key={activity.id}
                  data-testid="activity-feed-item"
                  data-target-url={targetUrl || ''}
                  className="feed-row"
                  role={targetUrl ? 'link' : undefined}
                  tabIndex={targetUrl ? 0 : undefined}
                  onClick={() => targetUrl && navigate(targetUrl)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && targetUrl) navigate(targetUrl); }}
                  style={{ cursor: targetUrl ? 'pointer' : 'default' }}
                >
                  <span className={`ws-score ${wsTone(tone)}`} aria-hidden>
                    {actorName ? wsInitials(actorName) : typeIcon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span className="ws-truncate" style={{ fontSize: 14.5, fontWeight: 550 }}>{activity.title}</span>
                      {count > 1 && (
                        <span data-testid="activity-group-count" className="ws-mono ws-faint" style={{ fontSize: 11.5, flexShrink: 0 }}>×{count}</span>
                      )}
                    </div>
                    <div className="ws-sub ws-truncate" style={{ marginTop: 2 }}>{activity.message}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ws-ink-2)' }}>{menuLabel || '알림'}</div>
                    <div className="ws-sub ws-num" style={{ fontSize: 12.5, marginTop: 2 }}>{formatTimeAgo(activity.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button type="button" variant="ghost" className="ws-btn ws-btn--blue" style={{ flex: 1 }} onClick={() => navigate('/notifications')}>
            전체 보기{activities.length > 0 ? ` ${activities.length}건` : ''} <ChevronRight size={15} aria-hidden />
          </Button>
          {activities.length > 0 && (
            <Tooltip label="모두 읽음">
              <Button type="button" variant="ghost" className="ws-btn ws-btn--icon" aria-label="모두 읽음" onClick={markAll}>
                <CheckCheck size={16} aria-hidden />
              </Button>
            </Tooltip>
          )}
          <Tooltip label="새로고침">
            <Button type="button" variant="ghost" className="ws-btn ws-btn--icon" aria-label="활동 새로고침" onClick={load}>
              <RefreshCw size={15} aria-hidden />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ActivityFeedCard;
